import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { FAST_API_BASE_URL } from "constants";
import { DEFAULT_PROVIDER, TEAM_SCHEDULING_PROVIDER } from "../utils/providerConfig";

const useSingleProvider = (domainId) => {
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [calendarUrl, setCalendarUrl] = useState("");
  const [hasExistingConnection, setHasExistingConnection] = useState(false);
  const [existingProvider, setExistingProvider] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [providerHistory, setProviderHistory] = useState({});
  const [loading, setLoading] = useState({ 
    fetch: false, 
    save: false, 
    check: false, 
    disconnect: false 
  });

  useEffect(() => {
    if (!domainId) {
      setSelectedProvider(DEFAULT_PROVIDER);
      setCalendarUrl("");
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
      setProviderHistory({});
      setLoading({ fetch: false, save: false, check: false, disconnect: false });
    }
  }, [domainId]);

  const fetchActiveProvider = useCallback(async () => {
    if (!domainId) return;

    try {
      setLoading(prev => ({ ...prev, check: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      if (res?.data?.resultCode === "0" && res.data.result?.provider) {
        const { provider, calendar_url } = res.data.result;
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(calendar_url);
        setSelectedProvider(provider);
        setCalendarUrl(calendar_url);
        setProviderHistory(prev => ({ ...prev, [provider]: calendar_url }));
      } else {
        setSelectedProvider(DEFAULT_PROVIDER);
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
      }
    } catch (err) {
      console.error("Failed to fetch active provider:", err);
      setSelectedProvider(DEFAULT_PROVIDER);
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
    } finally {
      setLoading(prev => ({ ...prev, check: false }));
    }
  }, [domainId]);

  const fetchProviderDetails = useCallback(async (providerId) => {
    if (!domainId || !providerId || providerId === existingProvider) return;

    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers/${providerId}`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );

      if (res?.data?.resultCode === "0" && res.data.result?.calendar_url) {
        setCalendarUrl(res.data.result.calendar_url);
        setProviderHistory(prev => ({ ...prev, [providerId]: res.data.result.calendar_url }));
      } else {
        setCalendarUrl("");
        setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
      }
    } catch (err) {
      console.error(`Failed to fetch provider details for ${providerId}:`, err);
      setCalendarUrl("");
      setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  }, [domainId, existingProvider]);

  const saveProvider = useCallback(async (provider, url) => {
    if (provider === TEAM_SCHEDULING_PROVIDER) {
      url = "";
    } else if (provider === "google-calendar") {
      url = "N/A";
    } else if (!provider || !url.trim()) {
      return { success: false, message: "Please select a provider and enter a valid URL" };
    }

    try {
      setLoading(prev => ({ ...prev, save: true }));
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        { provider, calendar_url: url.trim() || "N/A" },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(url.trim());
        setProviderHistory(prev => ({ ...prev, [provider]: url.trim() }));
        return { success: true, message: "Provider connected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to save provider");
      }
    } catch (err) {
      console.error("Failed to save provider:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to connect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  }, [domainId]);

  const disconnectProvider = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, disconnect: true }));
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
          data: { provider: existingProvider },
        }
      );

      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
        setProviderHistory(prev => ({ ...prev, [existingProvider]: "" }));
        setSelectedProvider(DEFAULT_PROVIDER);
        setCalendarUrl("");
        return { success: true, message: "Disconnected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Failed to disconnect:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to disconnect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, disconnect: false }));
    }
  }, [existingProvider, domainId]);

  return {
    selectedProvider,
    setSelectedProvider,
    calendarUrl,
    setCalendarUrl,
    hasExistingConnection,
    existingProvider,
    existingUrl,
    loading,
    fetchActiveProvider,
    fetchProviderDetails,
    saveProvider,
    disconnectProvider
  };
};

export default useSingleProvider;